import { Address, GiftDetails, GiftMemberTypes } from '@lib/types';
import { SubmitNewJoinFormData, ISubmitNewJoinFormDataRequest } from '../contracts';
import axios from 'axios';
import { createAxiosConfig } from '../mockHelper';
import { formatAddressLine } from '../../utils/helpers';

export const SubmitNewJoinFormDataApi =
  (endpoint: string): SubmitNewJoinFormData =>
  async (request: ISubmitNewJoinFormDataRequest) => {
    try {
      const lambdaRequest = mapFormDataToLambdaValidateMemberRequest(request);
      const constructedEndpoint = `${endpoint}`;
      const response = await axios.post(constructedEndpoint, lambdaRequest, createAxiosConfig(null, endpoint));

      return Promise.resolve(response?.data);
    } catch (e) {
      return Promise.reject({ error: e });
    }
  };

const mapFormDataToLambdaValidateMemberRequest: (data: ISubmitNewJoinFormDataRequest) => LambdaValidateMemberRequest = (
  data
) => {
  const filteredRequest: LambdaValidateMemberRequest = {
    newJoinSessionId: data.newJoinSessionId,
    lastName: data.lastName ?? '',
    firstName: data.firstName ?? '',
    gender: data.gender ?? '',
    residenceAddress: mapAddressToLambdaRequest(data.residenceAddress),
    emailAddress: data.email ?? '',
    mobileNumber: data.mobileNumber ?? '',
    mobileConsent: data.mobileConsent ?? null,
    skipAddressValidation: data.skipAddressValidation ?? false,
    skipDonorAddressValidation: data.skipDonorAddressValidation ?? false,
    ...(data.dateOfBirth ? { dateOfBirth: data.dateOfBirth } : {}),
  };
  if (data.mailingAddress !== undefined) {
    filteredRequest.mailingAddress = mapAddressToLambdaRequest(data.mailingAddress);
  }

  if (data?.giftDetails) {
    const giftDetails = data?.giftDetails;
    filteredRequest.giftDetails = {
      startDate: giftDetails?.startDate,
      isHoliday: giftDetails?.isHoliday,
      giftCardValue: giftDetails?.giftCardValue,
      giftName: giftDetails?.giftName,
      giftCardRecipient: giftDetails?.giftCardRecipient,
      membershipCardRecipient: giftDetails?.membershipCardRecipient,
      giftBillRecipientSelected: giftDetails?.giftBillRecipientSelected,
      messageCode: giftDetails?.messageCode,
      donor: {
        firstName: giftDetails?.donor?.firstName,
        lastName: giftDetails?.donor?.lastName,
        gender: giftDetails?.donor?.gender ?? '',
        residenceAddress: giftDetails?.donor?.residenceAddress
          ? mapAddressToLambdaRequest(giftDetails?.donor?.residenceAddress)
          : undefined,
        mailingAddress: giftDetails?.donor?.mailingAddress
          ? mapAddressToLambdaRequest(giftDetails?.donor?.mailingAddress)
          : undefined,
        email: giftDetails?.donor?.email,
        mobileNumber: giftDetails?.donor?.mobileNumber,
      },
    };
  }
  return filteredRequest;
};

export const mapAddressToLambdaRequest: (data: Address) => LambdaValidateMemberAddress = (data) => {
  const { zip, zipSuffix } = parseZipCode(data?.zipCode);
  return {
    addressLine1: formatAddressLine(data),
    city: data.city ?? '',
    state: data.state ?? '',
    zip: zip ?? '',
    zipSuffix: zipSuffix ?? '',
  };
};

const parseZipCode = (zipCode: any) => {
  const zipArray = zipCode?.split('-');
  const zip = zipArray[0].trim();
  const zipSuffix = zipArray.length > 1 ? zipArray[1].trim() : '';
  return { zip, zipSuffix };
};

interface LambdaGiftDonorRequest {
  firstName?: string;
  lastName?: string;
  gender: string;
  residenceAddress?: LambdaValidateMemberAddress;
  mailingAddress?: LambdaValidateMemberAddress;
  email?: string;
  mobileNumber?: string;
}

interface LambdaGiftDetailRequest {
  startDate?: string;
  isHoliday?: boolean;
  giftCardValue?: number;
  giftName?: string;
  giftCardRecipient?: GiftMemberTypes;
  membershipCardRecipient?: GiftMemberTypes;
  giftBillRecipientSelected?: boolean;
  messageCode?: string;
  donor?: LambdaGiftDonorRequest;
}

interface LambdaValidateMemberRequest {
  newJoinSessionId: string;
  lastName: string;
  firstName: string;
  gender: string;
  dateOfBirth?: string;
  residenceAddress: LambdaValidateMemberAddress;
  mailingAddress?: LambdaValidateMemberAddress;
  emailAddress: string;
  mobileNumber: string;
  mobileConsent: boolean | null;
  skipAddressValidation?: boolean;
  skipDonorAddressValidation?: boolean;
  giftDetails?: LambdaGiftDetailRequest;
}

interface LambdaValidateMemberAddress {
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
  zipSuffix?: string;
}
